import { createLogger } from "@/lib/logger";
import {
  supportsBufferedVoiceNotes,
  supportsVoiceNotePronunciationAnalysis,
} from "@/lib/voiceNoteCapabilities";
import {
  VOICE_NOTE_PRONUNCIATION_TOPIC,
  type VoiceNotePronunciationProvider,
  type VoiceNotePronunciationState,
  parseVoiceNotePronunciationPacket,
} from "@/lib/voiceNotePronunciation";
import {
  ConnectionState,
  type DataPacket_Kind,
  type Participant,
  type Room,
  RoomEvent,
} from "livekit-client";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("useVoiceNoteTurn");
const VOICE_NOTE_AUDIO_TOPIC = "chickytutor.voice-note.audio.v1";
const VOICE_NOTE_PROTOCOL_VERSION = "1";
const VOICE_NOTE_ANALYSER_FFT_SIZE = 256;
const PRONUNCIATION_ANALYSIS_PREFERENCE_KEY = "chickytutor:pronunciation-analysis-enabled";

/**
 * Converts a time-domain waveform to a stable, UI-friendly activity level.
 * The analyser only observes the recorder's existing stream; it never creates
 * another microphone capture or audio destination.
 */
export function getVoiceNoteAudioLevel(samples: Uint8Array): number {
  if (samples.length === 0) return 0;

  let sumOfSquares = 0;
  for (const sample of samples) {
    const normalized = (sample - 128) / 128;
    sumOfSquares += normalized * normalized;
  }

  const rms = Math.sqrt(sumOfSquares / samples.length);
  // Keep room tone still while allowing normal conversational speech to use
  // the expressive range of the composer-wide visual field.
  return Math.min(1, Math.max(0, (rms - 0.012) / 0.16));
}

function useVoiceNoteAudioLevel(stream: MediaStream | null): number {
  const [audioLevel, setAudioLevel] = useState(0);

  useEffect(() => {
    setAudioLevel(0);
    if (!stream || typeof window === "undefined") return;

    const AudioContextClass =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    let animationFrame = 0;
    let audioContext: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let isDisposed = false;

    try {
      audioContext = new AudioContextClass();
      source = audioContext.createMediaStreamSource(stream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = VOICE_NOTE_ANALYSER_FFT_SIZE;
      analyser.smoothingTimeConstant = 0.78;
      source.connect(analyser);
      if (audioContext.state === "suspended") {
        void audioContext.resume().catch((cause) => {
          logger.warn("Voice-note audio visualizer could not resume", {
            error: cause instanceof Error ? cause.message : String(cause),
          });
        });
      }

      const samples = new Uint8Array(analyser.fftSize);
      let smoothedLevel = 0;
      const sampleAudio = () => {
        if (isDisposed || !analyser) return;
        analyser.getByteTimeDomainData(samples);
        const targetLevel = getVoiceNoteAudioLevel(samples);
        smoothedLevel +=
          (targetLevel - smoothedLevel) * (targetLevel > smoothedLevel ? 0.38 : 0.18);
        const nextLevel = smoothedLevel < 0.012 ? 0 : Math.min(1, smoothedLevel);
        setAudioLevel((currentLevel) =>
          Math.abs(currentLevel - nextLevel) >= 0.012 ? nextLevel : currentLevel
        );
        animationFrame = window.requestAnimationFrame(sampleAudio);
      };

      animationFrame = window.requestAnimationFrame(sampleAudio);
    } catch (cause) {
      logger.warn("Voice-note audio visualizer unavailable", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
    }

    return () => {
      isDisposed = true;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      source?.disconnect();
      analyser?.disconnect();
      void audioContext?.close().catch(() => undefined);
    };
  }, [stream]);

  return audioLevel;
}

export type VoiceNoteTurnState =
  | "idle"
  | "starting"
  | "recording"
  | "reviewing"
  | "committing"
  | "cancelling"
  | "error";

interface UseVoiceNoteTurnOptions {
  room: Room | null;
  agentIdentity?: string;
  enabled: boolean;
  isConnected: boolean;
  isMuted: boolean;
  setMuted?: (muted: boolean) => void | Promise<void>;
  maxDurationSeconds?: number;
  reviewDurationSeconds?: number;
}

interface VoiceNoteRpcResponse {
  ok?: boolean;
  state?: string;
  error?: string;
  transcript?: string;
  voiceNoteId?: string;
  audioTopic?: string;
  protocolVersion?: string;
  pronunciationAnalysis?: {
    status?: "pending";
    provider?: VoiceNotePronunciationProvider;
  };
}

function createRequestPayload(extra: Record<string, unknown> = {}) {
  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `voice-note-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return JSON.stringify({ requestId, sentAtMs: Date.now(), ...extra });
}

function parseRpcResponse(value: string): VoiceNoteRpcResponse {
  try {
    return JSON.parse(value) as VoiceNoteRpcResponse;
  } catch {
    return { ok: false, error: "invalid_response" };
  }
}

export function useVoiceNoteTurn({
  room,
  agentIdentity,
  enabled,
  isConnected,
  isMuted,
  setMuted,
  maxDurationSeconds = 60,
  reviewDurationSeconds = 30,
}: UseVoiceNoteTurnOptions) {
  const posthog = usePostHog();
  const [state, setState] = useState<VoiceNoteTurnState>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [pronunciationAnalysisAvailable, setPronunciationAnalysisAvailable] = useState(false);
  const [pronunciationAnalysisEnabled, setPronunciationAnalysisEnabledState] = useState(true);
  const [pronunciationAnalysis, setPronunciationAnalysis] =
    useState<VoiceNotePronunciationState | null>(null);
  const stateRef = useRef<VoiceNoteTurnState>("idle");
  const mutedRef = useRef(isMuted);
  const mutedBeforeVoiceNoteRef = useRef(isMuted);
  const startedAtRef = useRef<number | null>(null);
  const reviewDeadlineRef = useRef<number | null>(null);
  const operationRef = useRef(0);
  const activeTargetRef = useRef<{ room: Room; identity: string } | null>(null);
  const pendingRecordingRef = useRef<{
    blob: Promise<Blob | null>;
    durationSeconds: number;
  } | null>(null);
  const activeVoiceNoteIdRef = useRef<string | null>(null);
  const latestCommittedVoiceNoteIdRef = useRef<string | null>(null);
  const dismissedPronunciationIdsRef = useRef(new Set<string>());
  const pronunciationAnalysisEnabledRef = useRef(true);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const recordingChunksRef = useRef<BlobPart[]>([]);
  const roomRef = useRef(room);
  const agentIdentityRef = useRef(agentIdentity);
  const setMutedRef = useRef(setMuted);
  const audioLevel = useVoiceNoteAudioLevel(recordingStream);

  useEffect(() => {
    roomRef.current = room;
    agentIdentityRef.current = agentIdentity;
    setMutedRef.current = setMuted;
  }, [agentIdentity, room, setMuted]);

  useEffect(() => {
    mutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(PRONUNCIATION_ANALYSIS_PREFERENCE_KEY);
      if (stored === "false") {
        pronunciationAnalysisEnabledRef.current = false;
        setPronunciationAnalysisEnabledState(false);
      }
    } catch {
      // A blocked storage API leaves the session-scoped default enabled.
    }
  }, []);

  useEffect(() => {
    const syncCapability = () => {
      const participant =
        room && agentIdentity ? room.remoteParticipants.get(agentIdentity) : undefined;
      setIsSupported(supportsBufferedVoiceNotes(participant?.attributes));
      setPronunciationAnalysisAvailable(
        supportsVoiceNotePronunciationAnalysis(participant?.attributes)
      );
    };
    syncCapability();
    if (!room) return;

    const handleAttributesChanged = (
      _changedAttributes: Record<string, string>,
      participant: Participant
    ) => {
      if (participant.identity === agentIdentity) syncCapability();
    };
    const handleParticipantChanged = (participant: Participant) => {
      if (participant.identity === agentIdentity) syncCapability();
    };

    room.on(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
    room.on(RoomEvent.ParticipantConnected, handleParticipantChanged);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantChanged);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
      room.off(RoomEvent.ParticipantConnected, handleParticipantChanged);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantChanged);
    };
  }, [agentIdentity, room]);

  useEffect(() => {
    if (!room) return;
    const handlePronunciationPacket = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: DataPacket_Kind,
      topic?: string
    ) => {
      if (topic !== VOICE_NOTE_PRONUNCIATION_TOPIC || participant?.identity !== agentIdentity) {
        return;
      }
      const packet = parseVoiceNotePronunciationPacket(payload);
      if (
        !packet ||
        !pronunciationAnalysisEnabledRef.current ||
        packet.voiceNoteId !== latestCommittedVoiceNoteIdRef.current ||
        dismissedPronunciationIdsRef.current.has(packet.voiceNoteId)
      ) {
        return;
      }
      setPronunciationAnalysis(packet);
    };

    room.on(RoomEvent.DataReceived, handlePronunciationPacket);
    return () => {
      room.off(RoomEvent.DataReceived, handlePronunciationPacket);
    };
  }, [agentIdentity, room]);

  const transition = useCallback((next: VoiceNoteTurnState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const isCurrentOperation = useCallback(
    (operation: number, expectedState?: VoiceNoteTurnState) => {
      const target = activeTargetRef.current;
      return (
        operation === operationRef.current &&
        (!expectedState || stateRef.current === expectedState) &&
        target?.room === roomRef.current &&
        target?.identity === agentIdentityRef.current &&
        target?.room.state === ConnectionState.Connected
      );
    },
    []
  );

  const applyMuted = useCallback(async (muted: boolean) => {
    const setter = setMutedRef.current;
    if (!setter || mutedRef.current === muted) return;
    await setter(muted);
    mutedRef.current = muted;
  }, []);

  const performRpc = useCallback(
    async (
      method: string,
      payload: Record<string, unknown> = {}
    ): Promise<VoiceNoteRpcResponse> => {
      const activeRoom = roomRef.current;
      const destinationIdentity = agentIdentityRef.current;
      if (!activeRoom || !destinationIdentity) {
        throw new Error("agent_not_ready");
      }

      const raw = await activeRoom.localParticipant.performRpc({
        destinationIdentity,
        method,
        payload: createRequestPayload(payload),
      });
      const response = parseRpcResponse(raw);
      if (!response.ok) {
        throw new Error(response.error || `${method}_failed`);
      }
      return response;
    },
    []
  );

  const stopLocalRecording = useCallback(async (): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return null;
    const chunks = recordingChunksRef.current;
    try {
      return await new Promise<Blob>((resolve, reject) => {
        const finish = () => resolve(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
        recorder.onstop = finish;
        recorder.onerror = () => reject(new Error("recording_failed"));
        if (recorder.state === "inactive") finish();
        else recorder.stop();
      });
    } finally {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      chunks.length = 0;
      // A late stop event must not clear a subsequent recorder's tracks or data.
      if (mediaRecorderRef.current === recorder) {
        mediaRecorderRef.current = null;
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        setRecordingStream(null);
      }
    }
  }, []);

  const prepareRecording = useCallback(() => {
    if (!pendingRecordingRef.current) {
      pendingRecordingRef.current = {
        durationSeconds:
          startedAtRef.current === null
            ? 0
            : Math.max(0, (Date.now() - startedAtRef.current) / 1000),
        blob: stopLocalRecording(),
      };
    }
    return pendingRecordingRef.current;
  }, [stopLocalRecording]);

  const cancelLocalRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder) recorder.ondataavailable = null;
    try {
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
    } catch {
      // Ignore recorder cleanup errors during cancel/reconnect recovery.
    }
    mediaRecorderRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    setRecordingStream(null);
    recordingChunksRef.current.length = 0;
    pendingRecordingRef.current = null;
    reviewDeadlineRef.current = null;
  }, []);

  const startLocalRecording = useCallback(
    async (operation: number) => {
      if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        throw new Error("recording_not_supported");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isCurrentOperation(operation)) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      mediaStreamRef.current = stream;
      const mimeType = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ].find((candidate) => MediaRecorder.isTypeSupported(candidate));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: BlobPart[] = [];
      recordingChunksRef.current = chunks;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      mediaStreamRef.current = stream;
      setRecordingStream(stream);
      mediaRecorderRef.current = recorder;
      recorder.start();
    },
    [isCurrentOperation]
  );

  const stopLiveKitMicrophoneTrack = useCallback(async () => {
    const activeRoom = roomRef.current;
    if (!activeRoom) return;
    await activeRoom.localParticipant.setMicrophoneEnabled(false, undefined, {
      stopMicTrackOnMute: true,
    } as Parameters<typeof activeRoom.localParticipant.setMicrophoneEnabled>[2]);
    mutedRef.current = true;
    const setter = setMutedRef.current;
    if (setter) {
      await setter(true);
    }
  }, []);

  const sendRecording = useCallback(
    async (blob: Blob, durationSeconds: number, operation: number) => {
      const activeRoom = roomRef.current;
      const destinationIdentity = agentIdentityRef.current;
      const voiceNoteId = activeVoiceNoteIdRef.current;
      if (!activeRoom || !destinationIdentity || !voiceNoteId) {
        throw new Error("voice_note_not_ready");
      }
      const bytes = new Uint8Array(await blob.arrayBuffer());
      if (!isCurrentOperation(operation)) throw new Error("voice_note_interrupted");
      const writer = await activeRoom.localParticipant.streamBytes({
        name: `${voiceNoteId}.webm`,
        topic: VOICE_NOTE_AUDIO_TOPIC,
        mimeType: blob.type || "audio/webm",
        totalSize: bytes.byteLength,
        destinationIdentities: [destinationIdentity],
        attributes: {
          voiceNoteId,
          protocolVersion: VOICE_NOTE_PROTOCOL_VERSION,
          durationSeconds: String(Number(durationSeconds.toFixed(3))),
        },
      });
      try {
        if (!isCurrentOperation(operation)) throw new Error("voice_note_interrupted");
        await writer.write(bytes);
      } finally {
        await writer.close();
      }
      return bytes.byteLength;
    },
    [isCurrentOperation]
  );

  const restorePreviousMuteState = useCallback(async () => {
    await applyMuted(mutedBeforeVoiceNoteRef.current);
  }, [applyMuted]);

  const cancelServerTurnBestEffort = useCallback(async () => {
    if (roomRef.current?.state !== ConnectionState.Connected) return;
    try {
      await performRpc("voice_note.cancel", { voiceNoteId: activeVoiceNoteIdRef.current });
    } catch {
      // Server watchdog/disconnect cleanup owns recovery if the agent RPC is unavailable.
    }
  }, [performRpc]);

  const start = useCallback(async () => {
    if (stateRef.current !== "idle" && stateRef.current !== "error") return;
    if (!enabled || !isSupported || !isConnected || !roomRef.current || !agentIdentityRef.current) {
      setError("Your tutor is still getting ready. Try again in a moment.");
      transition("error");
      return;
    }
    const recovering = stateRef.current === "error";
    const operation = ++operationRef.current;
    activeTargetRef.current = { room: roomRef.current!, identity: agentIdentityRef.current! };
    transition("starting");

    if (recovering) {
      cancelLocalRecording();
      try {
        await cancelServerTurnBestEffort();
        if (!isCurrentOperation(operation)) return;
        await restorePreviousMuteState();
      } catch (cause) {
        if (!isCurrentOperation(operation)) return;
        logger.error("Failed to restore microphone before voice-note retry", {
          error: cause instanceof Error ? cause.message : String(cause),
        });
        setError("Natural conversation is still recovering. Try again in a moment.");
        transition("error");
        return;
      }
    }

    if (!isCurrentOperation(operation)) return;
    mutedBeforeVoiceNoteRef.current = mutedRef.current;
    pendingRecordingRef.current = null;
    reviewDeadlineRef.current = null;
    setError(null);
    setElapsedSeconds(0);
    transition("starting");

    try {
      const startResponse = await performRpc("voice_note.start");
      if (!isCurrentOperation(operation)) return;
      if (!startResponse.voiceNoteId) {
        throw new Error("missing_voice_note_id");
      }
      activeVoiceNoteIdRef.current = startResponse.voiceNoteId;
      latestCommittedVoiceNoteIdRef.current = null;
      setPronunciationAnalysis(null);
      await stopLiveKitMicrophoneTrack();
      if (!isCurrentOperation(operation)) return;
      await startLocalRecording(operation);
      if (!isCurrentOperation(operation)) return;
      startedAtRef.current = Date.now();
      reviewDeadlineRef.current =
        startedAtRef.current +
        (Math.max(1, maxDurationSeconds) + Math.max(1, reviewDurationSeconds)) * 1000;
      transition("recording");
      posthog?.capture("voice_note_record_started");
    } catch (cause) {
      if (!isCurrentOperation(operation)) return;
      logger.error("Failed to start voice-note turn", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      await cancelServerTurnBestEffort();
      if (!isCurrentOperation(operation)) return;
      cancelLocalRecording();
      try {
        await restorePreviousMuteState();
      } catch {
        // Preserve the original start error in the UI.
      }
      if (!isCurrentOperation(operation)) return;
      setError(
        cause instanceof Error && cause.message === "agent_not_ready"
          ? "Your tutor is still getting ready. Try again in a moment."
          : "We couldn't start the voice note. Try again."
      );
      transition("error");
      posthog?.capture("voice_note_record_failed", { phase: "start" });
    }
  }, [
    cancelServerTurnBestEffort,
    cancelLocalRecording,
    enabled,
    isSupported,
    isConnected,
    isCurrentOperation,
    maxDurationSeconds,
    reviewDurationSeconds,
    performRpc,
    posthog,
    restorePreviousMuteState,
    startLocalRecording,
    stopLiveKitMicrophoneTrack,
    transition,
  ]);

  const cancel = useCallback(
    async (reason: "user" | "timeout" = "user") => {
      if (!["starting", "recording", "reviewing", "error"].includes(stateRef.current)) return;
      const fromReview = stateRef.current === "reviewing";
      const durationSeconds =
        pendingRecordingRef.current?.durationSeconds ??
        (startedAtRef.current === null
          ? 0
          : Math.max(0, (Date.now() - startedAtRef.current) / 1000));
      const operation = ++operationRef.current;
      transition("cancelling");
      cancelLocalRecording();
      try {
        await cancelServerTurnBestEffort();
        if (!isCurrentOperation(operation)) return;
        await restorePreviousMuteState();
        if (!isCurrentOperation(operation)) return;
        activeVoiceNoteIdRef.current = null;
        startedAtRef.current = null;
        setElapsedSeconds(0);
        setError(null);
        transition("idle");
        posthog?.capture("voice_note_record_cancelled", {
          duration_seconds: Number(durationSeconds.toFixed(2)),
          reason,
          from_review: fromReview,
        });
      } catch (cause) {
        if (!isCurrentOperation(operation)) return;
        logger.error("Failed to cancel voice-note turn", {
          error: cause instanceof Error ? cause.message : String(cause),
        });
        setError("The recording was interrupted. Your microphone is muted while we recover.");
        transition("error");
        posthog?.capture("voice_note_record_failed", { phase: "cancel" });
      }
    },
    [
      cancelServerTurnBestEffort,
      cancelLocalRecording,
      isCurrentOperation,
      posthog,
      restorePreviousMuteState,
      transition,
    ]
  );

  const stopForReview = useCallback(async () => {
    if (stateRef.current !== "recording") return;
    if (reviewDeadlineRef.current !== null && Date.now() >= reviewDeadlineRef.current) {
      await cancel("timeout");
      return;
    }
    const operation = operationRef.current;
    const draft = prepareRecording();
    setElapsedSeconds(Math.floor(draft.durationSeconds));
    transition("reviewing");
    posthog?.capture("voice_note_recording_limit_reached", {
      duration_seconds: Number(draft.durationSeconds.toFixed(2)),
    });
    try {
      const blob = await draft.blob;
      if (!isCurrentOperation(operation, "reviewing")) return;
      if (!blob?.size) throw new Error("empty_audio");
    } catch (cause) {
      // Send may already own this same stop promise; only its owner handles failure.
      if (!isCurrentOperation(operation, "reviewing")) return;
      await cancel();
      if (operationRef.current !== operation + 1) return;
      setError("We couldn't keep the voice note. Please record it again.");
      transition("error");
      logger.error("Failed to stop voice-note recording for review", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      posthog?.capture("voice_note_record_failed", { phase: "review" });
    }
  }, [cancel, isCurrentOperation, posthog, prepareRecording, transition]);

  const commit = useCallback(async () => {
    if (!["recording", "reviewing"].includes(stateRef.current)) return;
    // A suspended tab may receive a click before its overdue timer callback.
    if (reviewDeadlineRef.current !== null && Date.now() >= reviewDeadlineRef.current) {
      await cancel("timeout");
      return;
    }
    const fromReview = stateRef.current === "reviewing";
    const operation = operationRef.current;
    transition("committing");
    const draft = prepareRecording();
    const durationSeconds = draft.durationSeconds;

    try {
      const recording = await draft.blob;
      if (!isCurrentOperation(operation)) return;
      if (!recording || recording.size === 0) {
        throw new Error("empty_audio");
      }
      const bytes = await sendRecording(recording, durationSeconds, operation);
      if (!isCurrentOperation(operation)) return;
      const voiceNoteId = activeVoiceNoteIdRef.current;
      if (!voiceNoteId) throw new Error("missing_voice_note_id");
      latestCommittedVoiceNoteIdRef.current = voiceNoteId;
      const response = await performRpc("voice_note.commit", {
        voiceNoteId,
        durationSeconds: Number(durationSeconds.toFixed(3)),
        bytes,
        pronunciationAnalysisEnabled: pronunciationAnalysisEnabledRef.current,
      });
      if (!isCurrentOperation(operation)) return;
      const pronunciationProvider = response.pronunciationAnalysis?.provider;
      if (
        pronunciationAnalysisEnabledRef.current &&
        response.pronunciationAnalysis?.status === "pending" &&
        pronunciationProvider
      ) {
        setPronunciationAnalysis((current) =>
          current?.voiceNoteId === voiceNoteId && current.status !== "pending"
            ? current
            : {
                voiceNoteId,
                provider: pronunciationProvider,
                status: "pending",
              }
        );
      }
      await restorePreviousMuteState();
      if (!isCurrentOperation(operation)) return;
      pendingRecordingRef.current = null;
      reviewDeadlineRef.current = null;
      activeVoiceNoteIdRef.current = null;
      startedAtRef.current = null;
      setElapsedSeconds(0);
      setError(null);
      transition("idle");
      posthog?.capture("voice_note_record_committed", {
        duration_seconds: Number(durationSeconds.toFixed(2)),
        from_review: fromReview,
      });
    } catch (cause) {
      if (!isCurrentOperation(operation)) return;
      cancelLocalRecording();
      latestCommittedVoiceNoteIdRef.current = null;
      setPronunciationAnalysis(null);
      logger.error("Failed to commit voice-note turn", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      try {
        await restorePreviousMuteState();
      } catch {
        // The microphone remains safely muted if restoring fails.
      }
      if (!isCurrentOperation(operation)) return;
      setError("We couldn't send the voice note. Natural conversation will resume shortly.");
      transition("error");
      posthog?.capture("voice_note_record_failed", { phase: "commit" });
    }
  }, [
    cancel,
    cancelLocalRecording,
    isCurrentOperation,
    performRpc,
    posthog,
    prepareRecording,
    restorePreviousMuteState,
    sendRecording,
    transition,
  ]);

  useEffect(() => {
    if (state !== "recording") return;

    const updateElapsed = () => {
      if (stateRef.current !== "recording" || startedAtRef.current === null) return;
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
      if (Date.now() >= startedAtRef.current + Math.max(1, maxDurationSeconds) * 1000) {
        void stopForReview();
      }
    };
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 250);
    const autoStop = window.setTimeout(
      () => void stopForReview(),
      Math.max(
        0,
        (startedAtRef.current ?? Date.now()) + Math.max(1, maxDurationSeconds) * 1000 - Date.now()
      )
    );
    document.addEventListener("visibilitychange", updateElapsed);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(autoStop);
      document.removeEventListener("visibilitychange", updateElapsed);
    };
  }, [stopForReview, maxDurationSeconds, state]);

  useEffect(() => {
    if (state !== "reviewing" || reviewDeadlineRef.current === null) return;
    const expire = () => {
      if (
        stateRef.current === "reviewing" &&
        reviewDeadlineRef.current !== null &&
        Date.now() >= reviewDeadlineRef.current
      )
        void cancel("timeout");
    };
    const timer = window.setTimeout(expire, Math.max(0, reviewDeadlineRef.current - Date.now()));
    document.addEventListener("visibilitychange", expire);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [cancel, state]);

  useEffect(() => {
    if (
      (isConnected &&
        activeTargetRef.current?.room === room &&
        activeTargetRef.current?.identity === agentIdentity) ||
      !["starting", "recording", "reviewing", "committing", "cancelling"].includes(stateRef.current)
    ) {
      return;
    }
    ++operationRef.current;
    cancelLocalRecording();
    void stopLiveKitMicrophoneTrack();
    setError("The connection changed while recording. Please try again.");
    transition("error");
  }, [
    agentIdentity,
    room,
    cancelLocalRecording,
    isConnected,
    stopLiveKitMicrophoneTrack,
    transition,
  ]);

  useEffect(() => {
    return () => {
      ++operationRef.current;
      if (
        ["starting", "recording", "reviewing", "committing", "cancelling"].includes(
          stateRef.current
        )
      ) {
        cancelLocalRecording();
        void stopLiveKitMicrophoneTrack();
        const activeRoom = roomRef.current;
        const destinationIdentity = agentIdentityRef.current;
        if (activeRoom && destinationIdentity && activeRoom.state === ConnectionState.Connected) {
          void activeRoom.localParticipant
            .performRpc({
              destinationIdentity,
              method: "voice_note.cancel",
              payload: createRequestPayload({ voiceNoteId: activeVoiceNoteIdRef.current }),
            })
            .catch(() => undefined);
        }
      }
    };
  }, [cancelLocalRecording, stopLiveKitMicrophoneTrack]);

  const reset = useCallback(async () => {
    if (stateRef.current !== "error") return;
    try {
      if (stateRef.current === "error") {
        cancelLocalRecording();
        await cancelServerTurnBestEffort();
        await restorePreviousMuteState();
      }
      setError(null);
      setElapsedSeconds(0);
      startedAtRef.current = null;
      activeVoiceNoteIdRef.current = null;
      transition("idle");
    } catch {
      setError("Natural conversation is still recovering. Try again in a moment.");
    }
  }, [cancelServerTurnBestEffort, cancelLocalRecording, restorePreviousMuteState, transition]);

  const dismissPronunciationAnalysis = useCallback(() => {
    setPronunciationAnalysis((current) => {
      if (current) dismissedPronunciationIdsRef.current.add(current.voiceNoteId);
      return null;
    });
  }, []);

  const setPronunciationAnalysisEnabled = useCallback((nextEnabled: boolean) => {
    pronunciationAnalysisEnabledRef.current = nextEnabled;
    setPronunciationAnalysisEnabledState(nextEnabled);
    if (!nextEnabled) {
      latestCommittedVoiceNoteIdRef.current = null;
      setPronunciationAnalysis(null);
    }
    try {
      window.localStorage.setItem(PRONUNCIATION_ANALYSIS_PREFERENCE_KEY, String(nextEnabled));
    } catch {
      // The preference remains session-scoped when storage is unavailable.
    }
  }, []);

  return {
    state,
    elapsedSeconds,
    error,
    audioLevel,
    pronunciationAnalysis,
    pronunciationAnalysisAvailable,
    pronunciationAnalysisEnabled,
    isSupported,
    isAvailable: Boolean(
      enabled && isSupported && isConnected && room && agentIdentity && setMuted
    ),
    isActive: ["starting", "recording", "reviewing", "committing", "cancelling"].includes(state),
    start,
    commit,
    cancel,
    reset,
    dismissPronunciationAnalysis,
    setPronunciationAnalysisEnabled,
  };
}
